import { motion } from 'framer-motion';

import type { CompositeScenarioEvent } from '@/data/shared/safetyCompositeScenario';

import PeaksWaveform from './PeaksWaveform';
import { PauseIcon, PlayIcon } from './compositeEventIcons';
import { useClipAudio } from './useClipAudio';

type Props = {
  event: CompositeScenarioEvent;
  labelName: string;
  micName: string;
  leftPercent: number;
  widthPercent: number;
  layerIndex: number;
};

/**
 * ### CompositeEventTimelineFragment
 *
 * One detection on the timeline, drawn as the console draws it: a bar two
 * seconds wide at the moment it started, tinted by confidence, with the
 * clip's waveform inside and a play glyph on a white disc, so the bar reads
 * as something to press on the website rather than only as a console chart.
 * Clicking plays the clip and fills the bar as it plays. The sound type, the
 * microphone, and the confidence are read from the row and the tint, so the
 * bar carries them in its accessible name rather than as text.
 *
 * @param props The detection, its names, and its place on the row
 * @returns The rendered fragment element
 */
export default function CompositeEventTimelineFragment({
  event,
  labelName,
  micName,
  leftPercent,
  widthPercent,
  layerIndex,
}: Props) {
  const clip = useClipAudio(event.audioSrc);
  const confidencePercent = Math.round(event.confidence * 100);
  const tint =
    event.confidence < 0.33
      ? 'bg-[#6366f1]'
      : event.confidence < 0.66
        ? 'bg-[#4f46e5]'
        : 'bg-[#4338ca]';

  return (
    <motion.div
      layout
      transition={{ duration: 0.2, ease: [0.45, 0, 0.55, 1] }}
      className="absolute top-0 h-6"
      style={{
        left: `${leftPercent}%`,
        width: `${Math.max(0, Math.min(widthPercent, 100 - leftPercent))}%`,
        zIndex: 10 + layerIndex,
      }}
    >
      <button
        type="button"
        onClick={clip.toggle}
        disabled={clip.hasError}
        aria-label={`${labelName} · ${micName} · ${confidencePercent}%`}
        aria-pressed={clip.isPlaying}
        className={`relative flex h-full w-full cursor-pointer items-center gap-2 overflow-hidden rounded-[3px] border border-[rgba(255,255,255,0.3)] px-1 text-white shadow-sm transition-shadow hover:shadow-lg hover:ring-2 hover:ring-[rgba(99,102,241,0.45)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4f46e5] disabled:cursor-not-allowed disabled:opacity-40 ${tint}`}
      >
        <span
          className="pointer-events-none absolute inset-y-0 left-0 bg-[rgba(30,27,75,0.55)]"
          style={{ width: `${clip.progress * 100}%` }}
          aria-hidden="true"
        />
        <span className="pointer-events-none absolute inset-0 pl-6 pr-2">
          <PeaksWaveform
            peaks={clip.peaks}
            progress={clip.progress}
            height={24}
            baseColor="rgba(199,210,254,0.9)"
            progressColors={['#ffffff']}
            barWidth={1}
            barGap={1}
            className="block h-full w-full"
          />
        </span>
        <span className="relative z-10 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-white text-[#4338ca] shadow-sm">
          {clip.isPlaying ? (
            <PauseIcon className="h-2.5 w-2.5" />
          ) : (
            <PlayIcon className="ml-px h-2.5 w-2.5" />
          )}
        </span>
      </button>
    </motion.div>
  );
}
